import React from 'react';
import { 
  FaStar, 
  FaRegStar, 
  FaShare, 
  FaColumns, 
  FaEllipsisV,
  FaTimes
} from 'react-icons/fa';
import { FiEdit2 } from 'react-icons/fi';

// Title Header Component
const DesktopTitleHeader = ({
  error,
  setError,
  animateComponents,
  icon,
  title,
  breadcrumb,
  canEditIdea,
  isPinned,
  handlePinIdea,
  handleShareIdea,
  showRightSidebar,
  setShowRightSidebar,
  showOptionsMenu,
  handleOptionsMenuToggle,
  menuButtonRef,
  optionsMenuRef
}) => {
  return (
    <div className="hidden lg:block sticky top-0 z-10 bg-gradient-to-r from-[var(--primary-light)] to-[var(--primary)]">
      <div className="w-full px-2 sm:px-4 pt-6 pb-2 text-white">
        {error && (
          <div className="w-full p-3 border rounded-lg bg-danger-light mb-4 relative" style={{ borderColor: 'var(--danger)', color: 'var(--danger)' }}>
            <div className="pr-8">{error}</div>
            <button
              onClick={() => setError('')}
              className="absolute top-2 right-2 p-1 hover:bg-[var(--bg-hover)] rounded transition-colors"
              title="Dismiss error"
            >
              <FaTimes className="text-danger hover:text-[var(--danger-dark)]" />
            </button>
            {error === 'You do not have permission to view this idea.' && (
              <div className="mt-2 flex items-center gap-2">
                <span className="text-sm flex items-center text-[var(--text-secondary)]">
                  If you own this idea, your session may have expired. Try refreshing to re-authenticate.
                  <button
                    className="ml-2 text-white text-xs font-semibold py-0.5 px-2 rounded shadow-sm bg-[var(--primary)] hover:opacity-90 transition-opacity"
                    onClick={() => window.location.reload()}
                  >
                    Try to refresh
                  </button>
                </span>
              </div>
            )}
          </div>
        )}
        <div className="flex items-center gap-3 justify-between">
          <div className="flex flex-col gap-1 flex-1">
            <div className="flex items-center gap-3">
              <span className={`text-4xl ${animateComponents ? 'animate-spin-in' : 'opacity-0'}`}>{icon}</span>
              {breadcrumb && breadcrumb.length > 1 ? (
                <div className="flex items-center gap-1.5 flex-wrap">
                  {breadcrumb.map((item, index) => (
                    <React.Fragment key={item.id}>
                      {index > 0 && <span className="text-white/50 text-lg">/</span>}
                      <span 
                        className={`text-lg ${animateComponents ? 'animate-slide-in-left' : 'opacity-0'} ${
                          index === breadcrumb.length - 1 
                            ? 'font-bold text-[var(--text-primary)]' 
                            : 'text-white/60'
                        }`}
                        style={{ animationDelay: `${index * 50}ms` }}
                      >
                        {item.title}
                      </span>
                    </React.Fragment>
                  ))}
                </div>
              ) : (
                <h1 className={`text-2xl font-bold text-[var(--text-primary)] ${animateComponents ? 'animate-slide-in-left' : 'opacity-0'}`}>{title}</h1>
              )}
            </div>
          </div>
          
          <div className={`flex items-center gap-2 ${animateComponents ? 'animate-fade-in-up' : 'opacity-0'}`}>
            {canEditIdea && (
              <>
                <button
                  className="p-2 rounded-lg hover:bg-white/20 transition-all duration-200 hover:scale-110 active:scale-95"
                  onClick={handlePinIdea}
                  title={isPinned ? 'Unpin from sidebar' : 'Pin to sidebar'}
                >
                  {isPinned ? <FaStar /> : <FaRegStar className="hover:animate-wiggle" />}
                </button>
                <button
                  className="p-2 rounded-lg hover:bg-white/20 transition-all duration-200 hover:scale-110 active:scale-95 hover:animate-wiggle"
                  title="Share idea"
                  onClick={handleShareIdea}
                >
                  <FaShare />
                </button>
              </>
            )}
            <button
              className="p-2 rounded-lg hover:bg-white/20 transition-all duration-200 hover:scale-110 active:scale-95"
              onClick={() => setShowRightSidebar(!showRightSidebar)}
              title={showRightSidebar ? "Hide sidebar" : "Show sidebar"}
            >
              <FaColumns className={`transition-transform duration-300 ${!showRightSidebar ? 'rotate-90' : '-rotate-90'}`} />
            </button>
            <div className="relative" ref={optionsMenuRef}>
              <button
                ref={menuButtonRef}
                className="p-2 rounded-lg hover:bg-white/20 transition-all duration-200 hover:scale-110 active:scale-95"
                onClick={handleOptionsMenuToggle}
                title="More options"
              >
                <FaEllipsisV className={`transition-transform duration-300 ${showOptionsMenu ? 'rotate-90' : ''}`} />
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Description Section Component  
const DesktopDescription = ({
  animateComponents,
  description,
  canEditIdea,
  handleEditIdea,
  tags
}) => {
  return (
    <div className="hidden lg:block bg-[var(--bg-primary)]">
      <div className="pr-2 sm:pr-4 py-4">
        <div className="group relative">
          <p className={`text-[var(--text-primary)] whitespace-pre-line mb-2 pr-16 ${animateComponents ? 'animate-fade-in-up' : 'opacity-0'}`}>
            {description || 'No description provided'}
          </p>
          {canEditIdea && (
            <button
              className="absolute top-0 right-0 opacity-0 group-hover:opacity-100 transition-all duration-200 flex items-center gap-1 px-3 py-1 text-sm bg-[var(--primary)] text-white rounded-lg hover:opacity-90 hover:scale-105"
              onClick={handleEditIdea}
            >
              <FiEdit2 size={14} /> Edit
            </button>
          )}
        </div>
        <div className="flex overflow-x-auto gap-2 mb-2 pb-2 scrollbar-thin scrollbar-thumb-[var(--primary)] scrollbar-track-[var(--bg-secondary)]">
          {tags.map((tag, index) => (
            <span
              key={index}
              className={`px-3 py-0.5 bg-[var(--bg-hover)] text-[var(--text-primary)] rounded text-sm whitespace-nowrap hover:bg-[var(--primary)] hover:text-white transition-all duration-200 ${animateComponents ? 'animate-tag-slide-in-subtle' : 'opacity-0'}`}
            >
              {tag}
            </span>
          ))}
        </div>
      </div>
    </div>
  );
};

// Main DesktopHeader component (keeping for backward compatibility)
const DesktopHeader = (props) => {
  return (
    <>
      <DesktopTitleHeader {...props} />
      <DesktopDescription {...props} />
    </>
  );
};

export { DesktopTitleHeader, DesktopDescription };
export default DesktopHeader;
